import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';

import { EPublisherSolutionModelSubType } from 'common/contracts';
import { EFeatureFlag } from 'constants/enums';
import { UserDetails } from 'pages/Login/Login.types';

import { ESideBarContext, ESidebarGroupKey, SideBarGroup } from './types';

interface SidebarContextType {
  context: ESideBarContext;
  setContext: (context: ESideBarContext) => void;
  isCheckout: boolean;
  setIsCheckout: (isCheckout: boolean) => void;
  navItems: SideBarGroup[];
  setNavItems: (items: SideBarGroup[]) => void;
  collapsedGroups: Record<string, boolean>;
  toggleGroup: (group: string) => void;
  userDetails: UserDetails | null;
  featureFlags?: Record<EFeatureFlag, boolean>;
  companyFeatureFlags?: Record<EFeatureFlag, boolean>;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  currentProjectSubType: EPublisherSolutionModelSubType | null;
  setCurrentProjectSubType: (
    currentProjectSubType: EPublisherSolutionModelSubType | null
  ) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({
  children,
  initialUserDetails,
  initialFeatureFlags,
  initialCompanyFeatureFlags
}: {
  children: ReactNode;
  initialUserDetails: UserDetails | null;
  initialFeatureFlags?: Record<EFeatureFlag, boolean>;
  initialCompanyFeatureFlags?: Record<EFeatureFlag, boolean>;
}) {
  const [context, setContext] = useState<ESideBarContext>(
    ESideBarContext.COMPANY
  );
  const [isCheckout, setIsCheckout] = useState(false);
  const [currentProjectSubType, setCurrentProjectSubType] =
    useState<EPublisherSolutionModelSubType | null>(null);
  const [navItems, setNavItems] = useState<SideBarGroup[]>([]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    // Try to get saved state from session storage
    const savedState = sessionStorage.getItem('sidebarCollapsedGroups');
    if (savedState) {
      try {
        return JSON.parse(savedState);
      } catch (e) {
        console.error('Failed to parse saved sidebar state', e);
      }
    }

    // Return default state if nothing in storage or parsing failed
    return {
      [ESidebarGroupKey.Management]: false,
      [ESidebarGroupKey.Studio]: false,
      [ESidebarGroupKey.Offers]: false,
      [ESidebarGroupKey.Dashboard]: false,
      [ESidebarGroupKey.Projects]: false
    };
  });
  const [isLoading, setIsLoading] = useState(false);

  // Update session storage when state changes
  useEffect(() => {
    sessionStorage.setItem(
      'sidebarCollapsedGroups',
      JSON.stringify(collapsedGroups)
    );
  }, [collapsedGroups]);

  const toggleGroup = (group: string) => {
    setCollapsedGroups((prev: Record<string, boolean>) => ({
      ...prev,
      [group]: !prev[group as keyof typeof prev]
    }));
  };

  return (
    <SidebarContext.Provider
      value={{
        context,
        setContext,
        isCheckout,
        setIsCheckout,
        currentProjectSubType,
        setCurrentProjectSubType,
        navItems,
        setNavItems,
        collapsedGroups,
        toggleGroup,
        userDetails: initialUserDetails,
        featureFlags: initialFeatureFlags,
        companyFeatureFlags: initialCompanyFeatureFlags,
        isLoading,
        setIsLoading
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
